import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ListActiveTranslationJobsQuery,
  ListTranslationJobHistoryQuery,
  useListActiveTranslationJobsQuery,
  useListTranslationJobHistoryQuery,
} from 'src/generated/graphql';
import {
  ActiveTranslationJob,
  HistoricalTranslationJob,
  TrackFiltersState,
} from '../types';
import { TRACK_FETCH_BATCH_SIZE, TRACK_INITIAL_BATCHES } from '../constants';
import { END_OF_DAY_MILLIS } from 'src/common/constants';

/**
 * Converts a DateRangePicker value to epoch-milliseconds string boundaries for
 * filtering. The LangHub API's dateFrom/dateTo filters are epoch milliseconds
 * (stringified Long), so no unit conversion is applied.
 * Returns undefined for both bounds when no date range is set.
 * Uses calendar-accurate Date arithmetic for month/year boundaries.
 */
function getDateBounds(dateRange: TrackFiltersState['dateRange']): {
  dateFrom: string | undefined;
  dateTo: string | undefined;
} {
  if (!dateRange) return { dateFrom: undefined, dateTo: undefined };

  const now = new Date();
  const nowMillis = now.getTime();

  if (dateRange.type === 'relative') {
    const start = new Date(now);
    switch (dateRange.unit) {
      case 'day':
        start.setDate(start.getDate() - dateRange.amount);
        break;
      case 'week':
        start.setDate(start.getDate() - dateRange.amount * 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - dateRange.amount);
        break;
      default: // year
        start.setFullYear(start.getFullYear() - dateRange.amount);
    }
    return {
      dateFrom: String(start.getTime()),
      dateTo: String(nowMillis),
    };
  }

  if (dateRange.type === 'absolute') {
    return {
      dateFrom: dateRange.startDate
        ? String(new Date(dateRange.startDate).getTime())
        : undefined,
      // The picker yields a date-only (midnight) end bound; extend it to the
      // last millisecond of that day so the whole day is inclusive.
      dateTo: dateRange.endDate
        ? String(new Date(dateRange.endDate).getTime() + END_OF_DAY_MILLIS)
        : undefined,
    };
  }

  return { dateFrom: undefined, dateTo: undefined };
}

/** One server page of jobs, normalised out of a query result. */
interface JobPage<TItem> {
  items: TItem[];
  nextToken: string | null;
}

export interface JobWindow<TItem> {
  /** Every job accumulated so far, in server order. */
  items: TItem[];
  /** True only until the first batch has rendered. */
  initialLoading: boolean;
  /** True while a user-triggered `Load more` fetch is in flight. */
  isLoadingMore: boolean;
  /** True once the auto-chain has finished and a token still remains. */
  hasMore: boolean;
  /** Fetches one further batch and appends it. */
  loadMore: () => void;
  /** Drops the window and re-seeds from the next query result. */
  reset: () => void;
}

interface UseJobWindowParams<TData, TItem> {
  data: TData | undefined;
  loading: boolean;
  skip: boolean;
  /** Composite of every filter that invalidates the window. */
  resetKey: string;
  selectPage: (data: TData) => JobPage<TItem>;
  fetchNextBatch: (nextToken: string) => Promise<TData | undefined>;
}

/**
 * Accumulates one table's jobs into a single growing array: the first batch
 * renders immediately, then the remaining batches of the initial window are
 * chained in the background and appended. Client-side sorting and paging then
 * operate over the whole window rather than one server page.
 */
function useJobWindow<TData, TItem>({
  data,
  loading,
  skip,
  resetKey,
  selectPage,
  fetchNextBatch,
}: UseJobWindowParams<TData, TItem>): JobWindow<TItem> {
  const [items, setItems] = useState<TItem[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [batchesFetched, setBatchesFetched] = useState(0);
  const [isAutoChaining, setIsAutoChaining] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /** Set synchronously on reset; makes in-flight batches discard their result. */
  const autoChainAbortRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  // Held in refs so a caller re-creating these callbacks cannot restart the
  // chain, and so an in-flight batch always resolves against current props.
  const selectPageRef = useRef(selectPage);
  selectPageRef.current = selectPage;
  const fetchNextBatchRef = useRef(fetchNextBatch);
  fetchNextBatchRef.current = fetchNextBatch;

  const reset = useCallback(() => {
    autoChainAbortRef.current = true;
    setIsAutoChaining(false);
    setIsLoadingMore(false);
    isLoadingMoreRef.current = false;
    setItems([]);
    setToken(null);
    setSeeded(false);
    setBatchesFetched(0);
  }, []);

  // MUST stay above the chain effect: effects run in declaration order, so on a
  // filter change the abort flag is set before the chain effect could fire
  // another fetch with the pre-change token.
  useEffect(() => {
    reset();
  }, [resetKey, reset]);

  // Seed from the first batch. `loading` gates this so a result belonging to the
  // previous filters can never seed the new window.
  useEffect(() => {
    if (skip || seeded || loading || !data) return;

    const page = selectPageRef.current(data);
    setItems(page.items);
    setToken(page.nextToken);
    setSeeded(true);
    setBatchesFetched(1);

    if (page.nextToken && TRACK_INITIAL_BATCHES > 1) {
      autoChainAbortRef.current = false;
      setIsAutoChaining(true);
    }
  }, [data, loading, seeded, skip]);

  useEffect(() => {
    if (!isAutoChaining || !seeded) return;
    if (!token || batchesFetched >= TRACK_INITIAL_BATCHES) {
      setIsAutoChaining(false);
      return;
    }
    /* istanbul ignore if -- defensive: reset() clears isAutoChaining in the same commit, so the guard above returns first */
    if (autoChainAbortRef.current) {
      setIsAutoChaining(false);
      return;
    }

    let cancelled = false;

    const fetchBatch = async () => {
      try {
        const result = await fetchNextBatchRef.current(token);
        if (cancelled || autoChainAbortRef.current) return;
        if (!result) {
          setToken(null);
          setIsAutoChaining(false);
          return;
        }

        const page = selectPageRef.current(result);
        setItems((prev) => [...prev, ...page.items]);
        setToken(page.nextToken);
        setBatchesFetched((prev) => prev + 1);
        if (!page.nextToken) setIsAutoChaining(false);
      } catch (error) {
        /* istanbul ignore next -- untestable: Apollo's fetchMore resolves with { error } rather than rejecting */
        console.error('[useTrackData] Auto-chain fetch failed:', error);
        /* istanbul ignore next */
        setIsAutoChaining(false);
      }
    };

    fetchBatch();

    return () => {
      cancelled = true;
    };
  }, [isAutoChaining, seeded, token, batchesFetched]);

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || isAutoChaining || !token) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const result = await fetchNextBatchRef.current(token);
      if (autoChainAbortRef.current || !result) return;

      const page = selectPageRef.current(result);
      setItems((prev) => [...prev, ...page.items]);
      setToken(page.nextToken);
      setBatchesFetched((prev) => prev + 1);
    } catch (error) {
      /* istanbul ignore next -- untestable: see auto-chain above */
      console.error('[useTrackData] Load more failed:', error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [isAutoChaining, token]);

  return {
    items,
    initialLoading: !skip && loading && items.length === 0,
    isLoadingMore,
    hasMore: !isAutoChaining && !!token,
    loadMore,
    reset,
  };
}

const selectActivePage = (
  data: ListActiveTranslationJobsQuery,
): JobPage<ActiveTranslationJob> => ({
  items: (data.listActiveTranslationJobs?.items ??
    []) as ActiveTranslationJob[],
  nextToken: data.listActiveTranslationJobs?.nextToken ?? null,
});

const selectHistoricalPage = (
  data: ListTranslationJobHistoryQuery,
): JobPage<HistoricalTranslationJob> => ({
  items: (data.listTranslationJobHistory?.items ??
    []) as HistoricalTranslationJob[],
  nextToken: data.listTranslationJobHistory?.nextToken ?? null,
});

export function useTrackData(filters: TrackFiltersState) {
  // Memoize date bounds so they're computed once per filter change, not every
  // render. For relative ranges we snapshot Date.now() at apply-time via
  // applyGeneration — every Search click increments it, forcing recalculation.
  const { dateFrom, dateTo } = useMemo(
    () => getDateBounds(filters.dateRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.dateRange, filters.applyGeneration],
  );

  const skipQueries = !filters.namespace;

  const resetKey = [
    filters.namespace,
    filters.stringGroup,
    dateFrom ?? '',
    dateTo ?? '',
  ].join('|');

  const queryVariables = {
    namespaceId: filters.namespace,
    stringGroupId: filters.stringGroup || undefined,
    dateFrom,
    dateTo,
    pageSize: TRACK_FETCH_BATCH_SIZE,
    nextToken: undefined,
  };

  // --- Active translation jobs ---
  const {
    data: activeData,
    loading: activeLoading,
    error: activeError,
    refetch: refetchActiveJobs,
    fetchMore: fetchMoreActive,
  } = useListActiveTranslationJobsQuery({
    variables: queryVariables,
    skip: skipQueries,
    // 'network-only' ensures the mock link is always consulted (no stale cache
    // issues), and works correctly with WildcardMockLink in mock mode.
    fetchPolicy: 'network-only',
  });

  const fetchNextActiveBatch = useCallback(
    async (nextToken: string) => {
      const result = await fetchMoreActive({ variables: { nextToken } });
      return result.data;
    },
    [fetchMoreActive],
  );

  const activeWindow = useJobWindow<
    ListActiveTranslationJobsQuery,
    ActiveTranslationJob
  >({
    data: activeData,
    loading: activeLoading,
    skip: skipQueries,
    resetKey,
    selectPage: selectActivePage,
    fetchNextBatch: fetchNextActiveBatch,
  });

  // --- Historical translation jobs ---
  const {
    data: historicalData,
    loading: historicalLoading,
    error: historicalError,
    refetch: refetchHistoricalJobs,
    fetchMore: fetchMoreHistorical,
  } = useListTranslationJobHistoryQuery({
    variables: queryVariables,
    skip: skipQueries,
    fetchPolicy: 'network-only',
  });

  const fetchNextHistoricalBatch = useCallback(
    async (nextToken: string) => {
      const result = await fetchMoreHistorical({ variables: { nextToken } });
      return result.data;
    },
    [fetchMoreHistorical],
  );

  const historicalWindow = useJobWindow<
    ListTranslationJobHistoryQuery,
    HistoricalTranslationJob
  >({
    data: historicalData,
    loading: historicalLoading,
    skip: skipQueries,
    resetKey,
    selectPage: selectHistoricalPage,
    fetchNextBatch: fetchNextHistoricalBatch,
  });

  const resetActiveWindow = activeWindow.reset;
  const retryActiveJobs = useCallback(() => {
    resetActiveWindow();
    return refetchActiveJobs();
  }, [resetActiveWindow, refetchActiveJobs]);

  const resetHistoricalWindow = historicalWindow.reset;
  const retryHistoricalJobs = useCallback(() => {
    resetHistoricalWindow();
    return refetchHistoricalJobs();
  }, [resetHistoricalWindow, refetchHistoricalJobs]);

  return {
    activeJobs: activeWindow.items,
    activeJobsLoading: activeWindow.initialLoading,
    activeIsLoadingMore: activeWindow.isLoadingMore,
    activeHasMore: activeWindow.hasMore,
    loadMoreActiveJobs: activeWindow.loadMore,
    resetActiveJobs: activeWindow.reset,
    activeError,
    retryActiveJobs,

    historicalJobs: historicalWindow.items,
    historicalJobsLoading: historicalWindow.initialLoading,
    historicalIsLoadingMore: historicalWindow.isLoadingMore,
    historicalHasMore: historicalWindow.hasMore,
    loadMoreHistoricalJobs: historicalWindow.loadMore,
    resetHistoricalJobs: historicalWindow.reset,
    historicalError,
    retryHistoricalJobs,
  };
}
